import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Loader2 } from 'lucide-react';
import KpiStrip from './KpiStrip';
import { api } from '@/api/apiClient';

const OUTCOME_LABELS = { order: 'طلب', collection: 'تحصيل', info_only: 'متابعة', no_sale: 'بدون نتيجة' };
const OUTCOME_COLORS = { order: '#059669', collection: '#3B82F6', info_only: '#F59E0B', no_sale: '#9CA3AF' };
const TOOLTIP = { contentStyle: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '12px', direction: 'rtl' } };

export default function VisitsReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Prefer the SellGo endpoint: correct shape + scoped to the rep (a rep
      // sees only their own visits, a manager sees all). Fall back to the
      // legacy '/reports/visits' only if the SellGo one isn't deployed.
      let res;
      try {
        res = await api.get('/sellgo_report_visits', { date_from: dateFrom, date_to: dateTo });
      } catch {
        res = null;
      }
      if (!res?.success) {
        res = await api.get('/reports/visits', { date_from: dateFrom, date_to: dateTo });
      }
      setData(res?.success ? res.data : null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  // Defensive: the '/reports/visits' endpoint may return a partial or
  // differently-shaped payload. Reading `data.kpis.total_visits` directly when
  // `kpis` is undefined throws during render and blanks the whole page (no
  // error boundary). We normalise to safe defaults so the worst case is an
  // empty report, never a white screen.
  const kp = (data && data.kpis) || {};
  const byOutcome = (data && data.by_outcome) || {};
  const chart = data && Array.isArray(data.chart) ? data.chart : [];

  const kpis = data ? [
    { label: 'إجمالي الزيارات', value: String(kp.total_visits ?? 0), suffix: 'زيارة', to: '/visits' },
    { label: 'معدل الإنجاز', value: String(kp.strike_rate ?? 0), suffix: '%' },
    { label: 'زيارات منتجة', value: String(kp.productive ?? 0), suffix: 'زيارة', to: '/visits' },
    { label: 'طلبات ناتجة', value: String(kp.with_order ?? 0), suffix: 'طلب', to: '/orders' },
  ] : [];

  const piData = Object.entries(byOutcome).map(([k, v]) => ({
    name: OUTCOME_LABELS[k] || k,
    value: v,
    color: OUTCOME_COLORS[k] || '#9CA3AF'
  }));

  return (
    <div className="space-y-4">
      <KpiStrip kpis={kpis} loading={loading && !data} />

      <div className="bg-card border border-border rounded-2xl p-4">
        <h3 className="font-bold text-foreground mb-3">الزيارات اليومية</h3>
        {loading && !data ? <div className="h-48 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div> :
          chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={(v, n) => [v, n === 'count' ? 'زيارة' : 'طلبات']} />
                <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="زيارات" />
                <Bar dataKey="orders" fill="#059669" radius={[4, 4, 0, 0]} name="طلبات" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-muted-foreground py-10">لا توجد زيارات في هذا النطاق</p>
        }
      </div>

      {piData.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-3">نتائج الزيارات</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={piData} dataKey="value" cx="50%" cy="50%" outerRadius={65}>
                    {piData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP.contentStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {piData.map(d => (
                <div key={d.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                  <span className="text-xs text-muted-foreground">{d.name}</span>
                  <span className="text-xs font-bold mr-auto">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}